import axios from "axios";
import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import useAuth from "../hooks/useAuth";
import EditOrganMenuModal from "../modal/EditOrganMenuModal";
import { Material } from "@babylonjs/core";
import CryptoJS from "crypto-js";
import { TZDate } from "@date-fns/tz";
import HeaderDashboard from "./common/HeaderDashboard";
import { getModelPartRoute } from "../helpers";
import {
  cacheModel,
  clearModelCachesOnHardRefresh,
  deleteCachedModel,
  getCachedModel,
} from "../helpers/modelCache";

function PreviewModel() {
  const [progress, setProgress] = useState(0);
  const { level1 } = useParams();
  const [hasModelDetail, setHasModelDetail] = useState(false);
  const buttonsRef = useRef(null);
  const [hasMaterials, setHasMaterials] = useState(false);
  const [editedOrganMenu, setEditedOrganMenu] = useState(false);
  const [orderedMaterials, setOrderedMaterials] = useState([]);
  const [showEditOrganModal, setShowEditOrganModal] = useState({
    show: false, // initial values set to false and null
    id: null,
  });
  const [showProgress, setShowProgress] = useState(false);
  const modelRef = useRef(null);
  const modelAbortController = useRef(null);
  const navigate = useNavigate();
  const [presignedUrl, setPresignedUrl] = useState("");
  const { user } = useAuth();
  const [gender, setGender] = useState("Male Abdomen and Pelvis");
  const [genderGlbModelName, setGenderGlbModelName] = useState("male-abdomen-and-pelvis");
  const [showModel, setShowModel] = useState(true);
  const { pathname } = useLocation();
  const timeZone = "Australia/Sydney";
  const currentYear = new TZDate(Date.now(), timeZone).getFullYear();

  const model = pathname.split("/").pop();
  const MODEL_DECRYPT_KEY = process.env.REACT_APP_MODEL_DECRYPT_KEY;

  async function decryptArrayBuffer(buffer) {
  // Split IV and encrypted data from raw bytes
  const bytes = new Uint8Array(buffer);

  const ivBytes = bytes.slice(0, 16);
  const encryptedBytes = bytes.slice(16);

  const iv = CryptoJS.lib.WordArray.create(ivBytes);
  const encrypted = CryptoJS.lib.WordArray.create(encryptedBytes);

  const key = CryptoJS.enc.Utf8.parse(MODEL_DECRYPT_KEY);

  const decrypted = CryptoJS.AES.decrypt(
    { ciphertext: encrypted },
    key,
    {
      iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7
    }
  );

  // Convert WordArray -> Uint8Array
  const result = new Uint8Array(decrypted.sigBytes);

  for (let i = 0; i < decrypted.sigBytes; i++) {
    result[i] =
      (decrypted.words[i >>> 2] >>> (24 - (i % 4) * 8)) & 0xff;
  }

  /* console.log("Decrypted Size:", result.length);
  console.log(
    "Magic:",
    String.fromCharCode(...result.slice(0, 4))
  );
  console.log("First 16 bytes:", result.slice(0, 16)); */

  return result.buffer;
}
async function loadModelEnc(modelName, bypassCache = false) {
  if (modelAbortController.current) {
    modelAbortController.current.abort();
  }

  const controller = new AbortController();
  modelAbortController.current = controller;
  const { signal } = controller;

  try {
    setPresignedUrl("");
    setShowProgress(true);
    setProgress(0);

    let cached = null;
    if (!bypassCache) {
      try {
        cached = await getCachedModel(modelName);
      } catch (error) {
        console.warn("Could not read the cached model.", error);
      }
    }

    if (signal.aborted) return null;

    if (cached) {
      try {
        console.log("✅ Loaded model from cache");

        const buffer = await cached.arrayBuffer();
        const total = buffer.byteLength;
        const chunkSize = 1024 * 1024;
        const chunks = [];

        for (let offset = 0; offset < total; offset += chunkSize) {
          if (signal.aborted) return null;

          const end = Math.min(offset + chunkSize, total);
          chunks.push(buffer.slice(offset, end));
          setProgress(total ? end / total : 1);
          await new Promise((resolve) => setTimeout(resolve, 10));
        }

        const blobUrl = URL.createObjectURL(
          new Blob(chunks, { type: "model/gltf-binary" })
        );

        if (signal.aborted) {
          URL.revokeObjectURL(blobUrl);
          return null;
        }

        setProgress(1);
        setPresignedUrl(blobUrl);
        return blobUrl;
      } catch (error) {
        console.warn("Could not read the cached model; downloading a fresh copy.", error);
        try {
          await deleteCachedModel(modelName);
        } catch (deleteError) {
          console.warn("Could not remove the unreadable cached model.", deleteError);
        }
      }
    }

    console.log("⬇️ Downloading encrypted model:", modelName);

    const res = await fetch(
      `${process.env.REACT_APP_API_URL}/model/${modelName}`,
      {
        signal,
        cache: bypassCache ? "no-store" : "default",
      }
    );

    if (!res.ok) throw new Error("Failed to get model URL");

    const { url } = await res.json();
    if (signal.aborted) return null;

    const response = await fetch(url, {
      signal,
      cache: bypassCache ? "no-store" : "default",
    });
    if (!response.ok) throw new Error("Failed to download encrypted model");

    const totalSize = Number(response.headers.get("Content-Length")) || 0;
    let encryptedBuffer;

    if (response.body) {
      const reader = response.body.getReader();
      const chunks = [];
      let received = 0;

      while (true) {
        if (signal.aborted) {
          await reader.cancel();
          return null;
        }

        const { done, value } = await reader.read();
        if (done) break;

        chunks.push(value);
        received += value.length;
        if (totalSize) setProgress(received / totalSize);
      }

      encryptedBuffer = await new Blob(chunks).arrayBuffer();
    } else {
      encryptedBuffer = await response.arrayBuffer();
    }

    if (signal.aborted) return null;

    const decryptedBuffer = await decryptArrayBuffer(encryptedBuffer);
    if (signal.aborted) return null;

    const blob = new Blob([decryptedBuffer], {
      type: "model/gltf-binary",
    });
    const blobUrl = URL.createObjectURL(blob);

    if (signal.aborted) {
      URL.revokeObjectURL(blobUrl);
      return null;
    }

    setProgress(1);
    setPresignedUrl(blobUrl);

    try {
      await cacheModel(modelName, blob);
    } catch (error) {
      console.warn("Model loaded, but could not be saved to a persistent cache.", error);
    }

    return blobUrl;
  } catch (error) {
    if (error.name === "AbortError") {
      console.log("🛑 Model loading cancelled:", modelName);
      return null;
    }

    console.error("❌ Model loading failed:", error);
    throw error;
  }
}

async function loadModel(filename) {

  const cache = await caches.open("models-v1");

  // 1️⃣ Check cache first
  const cached = await cache.match(filename);

  if (cached) {
    setShowProgress(true);
    setProgress(0);
    console.log("✅ Loaded model from cache");

    const blob = await cached.blob();

    setProgress(1);

    return URL.createObjectURL(blob);
  }

  console.log("⬇ Downloading model...");

  setShowProgress(true);
  setProgress(0);

  // 2️⃣ Get metadata
  const metaRes = await fetch(
    `${process.env.REACT_APP_API_URL}/model-meta/${filename}`
  );

  if (!metaRes.ok) {
    throw new Error("Failed to get model metadata");
  }

  const { size } = await metaRes.json();

  const chunkSize = 4 * 1024 * 1024; // 4MB
  const maxParallel = 4;

  const totalChunks = Math.ceil(size / chunkSize);
  const chunks = new Array(totalChunks);

  let index = 0;
  let downloaded = 0;

  async function worker() {

    while (true) {

      const current = index++;

      if (current >= totalChunks) break;

      const start = current * chunkSize;
      const end = Math.min(start + chunkSize - 1, size - 1);

      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/model-chunk/${filename}?start=${start}&end=${end}`
      );

      if (!res.ok) {
        throw new Error(`Chunk ${current} failed (${res.status})`);
      }

      const buffer = await res.arrayBuffer();

      chunks[current] = buffer;

      downloaded += buffer.byteLength;

      setProgress(downloaded / size);
    }
  }

  await Promise.all(
    Array.from({ length: maxParallel }, () => worker())
  );

  const blob = new Blob(chunks, {
    type: "model/gltf-binary"
  });

  // 3️⃣ Save complete GLB into browser cache
  await cache.put(
    filename,
    new Response(blob, {
      headers: {
        "Content-Type": "model/gltf-binary"
      }
    })
  );

  console.log("✅ Model cached");

  return URL.createObjectURL(blob);
}

async function loadModel1(filename) {
  setShowProgress(true);
  setProgress(0);

  const metaRes = await fetch(
    `${process.env.REACT_APP_API_URL}/model-meta/${filename}`
  );

  if (!metaRes.ok) {
    throw new Error("Failed to fetch model metadata");
  }

  const { size } = await metaRes.json();

  const chunkSize = 4 * 1024 * 1024; // 4 MB
  const maxParallel = 4;

  const totalChunks = Math.ceil(size / chunkSize);
  const chunks = new Array(totalChunks);

  let index = 0;
  let downloadedBytes = 0;

  async function worker() {
    while (true) {
      const current = index++;

      if (current >= totalChunks) {
        break;
      }

      const start = current * chunkSize;
      const end = Math.min(start + chunkSize - 1, size - 1);

      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/model-chunk/${filename}?start=${start}&end=${end}`
      );

      if (!res.ok) {
        throw new Error(`Failed to download chunk ${current}`);
      }

      const buffer = await res.arrayBuffer();

      chunks[current] = buffer;

      downloadedBytes += buffer.byteLength;

      // Never allow progress to decrease
      setProgress(prev => {
        const next = downloadedBytes / size;
        return Math.max(prev, next);
      });
    }
  }

  const workers = Array.from(
    { length: maxParallel },
    () => worker()
  );

  await Promise.all(workers);

  // Ensure progress reaches 100%
  setProgress(1);

  const blob = new Blob(chunks, {
    type: "model/gltf-binary",
  });
  return URL.createObjectURL(blob);
}

  const refreshOrganMenuWithMaterials = async (updatedMaterials) => {
    const modelMaterials = modelRef.current.model.materials;
    const orderedModelMaterials = manageMaterialFromModelAndDB(
      modelMaterials,
      updatedMaterials
    );

    // console.log("refreshOrganMenuWithMaterials modelMaterials", modelMaterials);
    // console.log("refreshOrganMenuWithMaterials orderedModelMaterials", orderedModelMaterials);
    // console.log("refreshOrganMenuWithMaterials orderedModelMaterials", orderedModelMaterials);

    if (!orderedModelMaterials.length) return;
    // ✅ wait for model detail to refresh
    // await fetchModelDetail();
    setEditedOrganMenu(orderedModelMaterials);
    loadOrganMenuFromDB(orderedModelMaterials);
  };

  async function updateMaterialsIfMissing(modelDetail, materials) {
    if (!materials?.length) return;
    if (!modelDetail) modelDetail = await fetchModelDetail();

    if (modelDetail.materials.length <= 0) {
      const materialConfig = materials.map((m, order) => ({
        originalName: m.name,
        displayName: m.name,
        order,
      }));

      try {
        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}/update-glb-model-material`,
          { _id: modelDetail.id, materials: materialConfig }
        );

        const orderedModelMaterials = manageMaterialFromModelAndDB(
          materials,
          response.data.materials
        );

        setEditedOrganMenu(orderedModelMaterials);
        setOrderedMaterials(orderedModelMaterials);
      } catch (err) {
        console.error("Failed to update materials:", err);
      }
    }
  }

  function manageMaterialFromModelAndDB(modelMaterials, dbMaterials) {
    if (!Array.isArray(modelMaterials) || !Array.isArray(dbMaterials)) return [];

    const modelMaterialMap = {};
    modelMaterials.forEach((mat) => (modelMaterialMap[mat.name] = mat));

    return [...dbMaterials]
      .sort((a, b) => a.order - b.order)
      .map((dbMat) => {
        const modelMat = modelMaterialMap[dbMat.originalName];
        if (!modelMat) return null;

        modelMat.displayName = dbMat.displayName;
        modelMat.originalName = dbMat.originalName;
        modelMat.order = dbMat.order;
        modelMat.visible = modelMat.visible ?? true;

        return modelMat;
      })
      .filter(Boolean);
  }

  function getLabelText(matName) {
    return matName
      .replace(/^adskMat/i, "")
      .replace(/_?BaseColor\d*/gi, "")
      .replace(/_?Base_Coloruu\d*/gi, "")
      .replace(/_?Color_mapehg\d*/gi, "")
      .replace(/_?4t54\d*/gi, "")
      .replace(/_?Bigt\d*/gi, "")
      .replace(/^M_?17_*/i, "")
      .replace(/\d+$/g, "")
      .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
      .replace(/[_-]/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .replace(/\bperitonum\b/i, "Peritoneum")
      .replace(/\b\w/g, (letter) => letter.toUpperCase());
  }

  function loadOrganMenuFromDB(materials) {
    if (!materials?.length) return;
    if (!buttonsRef.current) return;

    const menu = buttonsRef.current;
    menu.innerHTML = "";

    const heading = document.createElement("div");
    heading.className = "organ-menu-heading";

    const title = document.createElement("span");
    title.textContent = "Organs";
    heading.appendChild(title);

    const count = document.createElement("span");
    count.className = "organ-menu-count";
    count.textContent = String(materials.length);
    heading.appendChild(count);
    menu.appendChild(heading);

    const list = document.createElement("div");
    list.className = "organ-menu-list";

    const footer = document.createElement("div");
    footer.className = "organ-menu-footer";

    const visibleCount = document.createElement("span");
    const toggleAll = document.createElement("button");
    toggleAll.type = "button";
    toggleAll.className = "organ-menu-toggle-all";
    footer.append(visibleCount, toggleAll);

    const rows = materials.map((mat, index) => {
      const rawName =
        mat.displayName?.trim() || mat.name || mat.originalName || "";
      const labelText =
        (rawName && getLabelText(rawName)) || `Part ${index + 1}`;
      mat.visible = mat.visible !== false;

      if (!mat._originalBaseColor && mat.pbrMetallicRoughness) {
        const base = mat.pbrMetallicRoughness.baseColorFactor || [1, 1, 1, 1];
        mat._originalBaseColor = [...base];
      }

      const row = document.createElement("div");
      row.className = "organ-menu-row";

      const dot = document.createElement("span");
      dot.className = `organ-menu-dot organ-menu-dot-${index % 8}`;

      const partRoute = getModelPartRoute(labelText, level1);
      const label = document.createElement(partRoute ? "a" : "span");
      label.className = `organ-menu-label${partRoute ? " organ-menu-label-link" : ""}`;
      label.textContent = labelText;
      label.title = labelText;
      if (partRoute) {
        label.href = `/${partRoute}`;
        label.target = "_blank";
        label.rel = "noopener noreferrer";
      }

      const toggle = document.createElement("button");
      toggle.type = "button";
      toggle.className = "organ-menu-switch";
      toggle.setAttribute("role", "switch");
      toggle.setAttribute("aria-label", `Show ${labelText}`);
      toggle.classList.toggle("is-visible", mat.visible);
      toggle.setAttribute("aria-checked", String(mat.visible));
      row.classList.toggle("is-hidden", !mat.visible);

      const setVisibility = (visible, refreshMenu = true) => {
        mat.visible = visible;
        if (
          mat.originalName === "adskMatPeritonum" ||
          mat.originalName === "adskMatLungs"
        ) {
          mat.setAlphaMode("BLEND");
          mat.transparencyMode = Material.MATERIAL_ALPHABLEND;
          mat.alpha = 0.4;
        } else {
          mat.setAlphaMode("MASK");
          mat.setAlphaCutoff(0.5);
        }

        if (mat._originalBaseColor) {
          const [r, g, b, a] = mat._originalBaseColor;
          mat.pbrMetallicRoughness?.setBaseColorFactor([
            r,
            g,
            b,
            visible ? a : 0
          ]);
        }

        toggle.classList.toggle("is-visible", visible);
        toggle.setAttribute("aria-checked", String(visible));
        row.classList.toggle("is-hidden", !visible);
        if (refreshMenu) updateMenuState();
      };

      toggle.addEventListener("click", () => setVisibility(mat.visible === false));
      row.append(dot, label, toggle);
      list.appendChild(row);

      return { mat, setVisibility };
    });

    function updateMenuState() {
      const visibleMaterials = rows.filter(
        ({ mat }) => mat.visible !== false
      ).length;
      visibleCount.textContent = `${visibleMaterials} of ${materials.length} visible`;
      toggleAll.textContent =
        visibleMaterials === materials.length ? "Hide all" : "Show all";
      toggleAll.setAttribute("aria-label", toggleAll.textContent);
    }

    toggleAll.addEventListener("click", () => {
      const showAll = rows.some(({ mat }) => mat.visible === false);
      rows.forEach(({ setVisibility }) => setVisibility(showAll, false));
      updateMenuState();
    });

    menu.append(list, footer);
    updateMenuState();
    menu.classList.remove("open");

    const toggleBtn = document.getElementById("variantToggleBtn");
    if (toggleBtn) {
      toggleBtn.onclick = (e) => {
        e.stopPropagation();
        menu.classList.toggle("open");
      };
    }
  }

  const onLoad = (detail) => {
    // setProgress(1);

    // hide loader after model actually renders
    setTimeout(() => {
      setShowProgress(false);
    }, 100);

    const model = modelRef.current;
    if (!model?.model) return;

    const materials = model.model.materials;
    const hasMats = Array.isArray(materials)
      ? materials.length > 0
      : Object.keys(materials || {}).length > 0;

    setHasMaterials(hasMats);
    if (!hasMats) return;

    const dbMaterials = detail?.materials || [];

    if (!dbMaterials.length) {
      updateMaterialsIfMissing(detail, materials);
    }

    const orderedModelMaterials = manageMaterialFromModelAndDB(materials, dbMaterials);
    if (!orderedModelMaterials.length) return;

    setOrderedMaterials(orderedModelMaterials);
  };

  const onProgress = (e) => {
    if (e.detail.totalProgress < 1) {
      setShowProgress(true);
      setProgress(e.detail.totalProgress);
    }
  };

  async function getModelDetail() {
    const payload = {
      level1: "dashboard",
      level2: "anatomy-full-body"  // <-- always correct
    };

    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/get-glb-model-detail`,
      payload
    );

    return response.data;
  }

  const fetchModelDetail = async () => {
    try {
      const bypassCache = await clearModelCachesOnHardRefresh();
      const modelDetail = await getModelDetail();

      // ❌ No data in DB
      if (!modelDetail || !modelDetail.file_name || modelDetail.file_name === "coming-soon") {
        setHasModelDetail(null);
        setShowModel(false);
        setPresignedUrl("");   // important: clear old model
        return null;
      }

      // ✅ Valid model
      setHasModelDetail(modelDetail);
      setShowModel(true);

      await loadModelEnc(
        modelDetail.file_name.replace(".glb", ""),
        bypassCache
      );

      return modelDetail;
    } catch (error) {
      console.error("Failed to fetch model detail", error);
      setHasModelDetail(null);
      setShowModel(false);
      setPresignedUrl("");
      return null;
    }
  };

  useEffect(() => {
    if (!level1 || !model) return;

    const init = async () => {
      setShowProgress(true);
      setShowModel(true);
      setPresignedUrl("");

      const detail = await fetchModelDetail();
      if (!detail) {
        setShowModel(false);
        return null;
      }

      const onLoadWithDetail = () => onLoad(detail);

      modelRef.current?.addEventListener("load", onLoadWithDetail);
      // modelRef.current?.addEventListener("progress", onProgress);

      return () => {
        modelRef.current?.removeEventListener("load", onLoadWithDetail);
        // modelRef.current?.removeEventListener("progress", onProgress);
      };
    };

    init();
  }, [level1, model]);

  useEffect(() => {
    if (model === "male-abdomen-and-pelvis") {
      setGender("Male Abdomen and Pelvis");
      setGenderGlbModelName("male-abdomen-and-pelvis");
    }

    if (model === "female-abdomen-and-pelvis") {
      setGender("Female Abdomen and Pelvis");
      setGenderGlbModelName("female-abdomen-and-pelvis");
    }
  }, [model]);


  const handleGenderChange = (modelName) => {
    navigate(`/${level1}/${modelName}`);
  };

  useEffect(() => {
    if (!hasMaterials) return;
    if (!buttonsRef.current) return;
    if (!orderedMaterials.length) return;

    loadOrganMenuFromDB(orderedMaterials);
  }, [hasMaterials, orderedMaterials]);



  const editOrganMenuModelClick = (id) => {
    setShowEditOrganModal({
      show: true,
      id,
    });
  };
  return (
    <>
      {hasModelDetail && (
        <EditOrganMenuModal
          modal={showEditOrganModal.show}
          setModal={setShowEditOrganModal}
          id={hasModelDetail.id}
          materials={editedOrganMenu ? editedOrganMenu : hasModelDetail.materials}
          onMaterialsUpdated={refreshOrganMenuWithMaterials}
        />
      )}
      <div className="w-100 h-100 bg-green5">
        <HeaderDashboard />
        <main className="main-section mx-4 rounded-top-3 bg-white">
          <div className="h-100">
            <div className="row bg-green4 rounded-top-3 m-0 content-header position-relative">
              <div className="position-absolute d-flex w-auto top-0 start-50 translate-middle-x mt-2 align-items-center justify-content-center text-green1">
                <span className="fw-bold">{level1 == "abdomen" ? "2.0 Abdomen" : "3.0 Chest"}</span>
              </div>
              <div className="col-12 d-flex d-xl-none content-header-top"></div>
              <div className="col-6 d-flex align-items-center justify-content-start">
                <button
                  className="btn btn-sm btn-link text-decoration-none fw-bold"
                  onClick={() => navigate(-1)}
                >
                  <span className="icon-left"></span> Previous
                </button>
              </div>
              {/* <div className="col-6 d-flex align-items-center justify-content-end dropdown">
                <button
                  className="btn btn-secondary dropdown-toggle fw-bold"
                  type="button"
                  id="dropdownMenuButton2"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  {gender}
                </button>

                <ul
                  className="dropdown-menu dropdown-menu-end"
                  aria-labelledby="dropdownMenuButton2"
                >
                  <li>
                    <button
                      type="button"
                      className={`dropdown-item fs-7 fw-bolder ${model === "male-abdomen-and-pelvis" ? "active" : ""
                        }`}
                      onClick={() => handleGenderChange("male-abdomen-and-pelvis")}
                    >
                      Male Abdomen and Pelvis
                    </button>
                  </li>

                  <li>
                    <button
                      type="button"
                      className={`dropdown-item fs-7 fw-bolder ${model === "female-abdomen-and-pelvis" ? "active" : ""
                        }`}
                      onClick={() => handleGenderChange("female-abdomen-and-pelvis")}
                    >
                      Female Abdomen and Pelvis
                    </button>
                  </li>
                </ul>
              </div> */}
            </div>
            <div
              className={"height-scroll bg-grey-light overflow-hidden "}
              style={{ position: "relative" }}
            >
              {showModel && (
                <>
                  <div className="d-flex justify-content-between align-items-start w-100 menu-main-div">
                    {/* Overlay menu */}
                    {hasMaterials && (
                      <>
                        <div className="model-overlay-menu row-sm-4" style={{
                          position: "absolute",
                          right: "12px",
                          zIndex: 10
                        }}>
                          <div className="d-flex gap-2">
                            {/* <button
                              className="toggle-btn icon-btn"
                              onClick={() => editOrganMenuModelClick(hasModelDetail.id)}
                            >
                              <i className="bi bi-pencil-square"></i>
                            </button> */}

                            <button
                              id="variantToggleBtn"
                              className="toggle-btn icon-btn"
                            >
                              <span
                                className="material-symbols-outlined"
                                style={{ fontSize: "20px" }}
                              >
                                menu
                              </span>
                            </button>
                          </div>

                          <div
                            id="variantButtons"
                            ref={buttonsRef}
                            className="model-overlay-buttons organ-menu"
                          />
                        </div>
                      </>
                    )}

                  </div>
                  <div className="model-object bg-grey-light h-100 w-100">
                    <div className="w-100 h-100 d-flex">
                      <model-viewer
                        className="model-viewer"
                        camera-orbit="90deg 90deg 105%"
                        src={presignedUrl}
                        environment-image="/images/hdri/neutral.hdr"
                        alt="Liver"
                        camera-controls
                        ref={modelRef}
                        interaction-prompt="none"
                      >
                        <div slot="poster" className="position-relative h-100">
                          <div className="position-absolute top-50 start-50 translate-middle"></div>
                        </div>
                        <div
                          slot="progress-bar"
                          className={
                            "position-relative h-100 " +
                            (showProgress ? "d-block" : "d-none")
                          }
                        >
                          <div className="position-absolute top-50 start-50 translate-middle">
                            <div className="lds-ring">
                              <div></div>
                              <div></div>
                              <div></div>
                              <div></div>
                            </div>
                            <div className="position-absolute top-50 start-50 translate-middle">
                              <span className="fw-bold text-secondary">
                                {Math.trunc(progress * 100)}%
                              </span>
                            </div>
                          </div>
                        </div>
                      </model-viewer>
                    </div>
                  </div>

                </>
              )}
              <div
                className={`w-100 h-100 bg-green1 position-relative justify-content-center align-items-center ${showModel ? "d-none" : "d-flex"
                  }`}
              >
                <div className="position-absolute z-0 w-100 h-100 coming-soon-bg1"></div>
                <div className="position-absolute z-1 w-100 h-100 coming-soon-bg2"></div>
                <div className="position-absolute z-2 w-100 h-100 d-flex justify-content-center align-items-center">
                  <h1 className="text-white fw-bold">Model Coming Soon</h1>
                </div>
              </div>
            </div>
            <footer className="d-flex justify-content-between px-4 w-100 main-footer bg-green1">
              <div className="d-flex align-items-center">
                <img
                  src="/images/logo/anatomy-unlocked-logo-horizontal-white.svg"
                  alt=""
                  className=""
                />
                <div className="d-flex align-items-center ps-5">
                  <span className="text-white fs-8">Built by</span>
                  <img
                    src="/images/logo/vection-logo-white.svg"
                    style={{ width: "5rem" }}
                    alt=""
                    className="ps-2"
                  />
                </div>
                <Link to="/collaborators" className="ms-5 text-white fs-8">
                  Collaborators
                </Link>
                {user && (
                  <a
                    href={"/instructions.pdf"}
                    className="ms-3 text-white fs-8"
                    target="_blank"
                  >
                    Instructions
                  </a>
                )}
              </div>

              <div className="d-flex align-items-center fs-8 text-white">
                <div className="me-3">
                  <a
                    href="https://www.iubenda.com/privacy-policy/43897133"
                    className="iubenda-white iubenda-noiframe iubenda-embed iubenda-noiframe text-white"
                    title="Privacy Policy "
                  >
                    Privacy Policy
                  </a>
                </div>
                &copy; Copyright {currentYear}
              </div>
            </footer>
          </div>
        </main>
      </div>
    </>
  );
}

export default PreviewModel;
